import { useQuery } from "@tanstack/react-query";
import api from "@/services/api";
import { queryKeys } from "@/queryKeys";
import {
  FunderDashboardData,
  PipelineStage,
  PortfolioMetrics,
  DashboardKPIs,
  DashboardAnalytics,
} from "@/types/dashboard";
import { AppStatus } from "@/types/borrower";

// Pipeline stage display metadata (label + color) keyed by app_status value
const PIPELINE_META: Record<string, { label: string; color: string }> = {
  funder_review: { label: "Funder Review", color: "#1976d2" },
  contract_ready: { label: "Contract Ready", color: "#ed6c02" },
  final_underwriting: { label: "Final Underwriting", color: "#9c27b0" },
};

// Pipeline stage ordering
const PIPELINE_ORDER = ["funder_review", "contract_ready", "final_underwriting"];

interface RawPipelineStage {
  count: number;
  borrowers: Array<{
    id: number;
    company_name: string;
    owner_name: string;
    broker_email: string;
    broker_name?: string;
    created_at: string;
  }>;
}

interface RawResponse {
  kpis: {
    total_pipeline: number;
    active_deals: number;
    approval_rate: number;
    total_volume: number;
    total_brokers: number;
  };
  pipeline: Record<string, RawPipelineStage>;
  portfolio: {
    active_repayments: number;
    total_funded: number;
    daily_collections: number;
    weekly_collections: number;
    avg_factor_rate: number;
    avg_leverage: number;
    repayment_deals: Array<{
      borrower_id: number;
      company_name: string;
      offer_amount: number | null;
      daily_payment: number | null;
      factor_rate: number | null;
      term_days: number | null;
    }>;
  };
  analytics: DashboardAnalytics;
}

function transformResponse(raw: RawResponse): FunderDashboardData {
  // Transform pipeline: dict keyed by status → ordered array with label/color
  const pipeline: PipelineStage[] = PIPELINE_ORDER.map((status) => {
    const stage = raw.pipeline[status] ?? { count: 0, borrowers: [] };
    const meta = PIPELINE_META[status] ?? { label: status, color: "#9e9e9e" };
    return {
      status: status as AppStatus,
      label: meta.label,
      color: meta.color,
      count: stage.count,
      borrowers: stage.borrowers,
    };
  });

  // Transform portfolio: snake_case → camelCase
  const portfolio: PortfolioMetrics = {
    activeRepayments: raw.portfolio.active_repayments,
    totalFunded: raw.portfolio.total_funded,
    dailyCollections: raw.portfolio.daily_collections,
    weeklyCollections: raw.portfolio.weekly_collections,
    avgFactorRate: raw.portfolio.avg_factor_rate,
    avgLeverage: raw.portfolio.avg_leverage,
    repaymentDeals: raw.portfolio.repayment_deals,
  };

  // Transform KPIs: snake_case → camelCase
  const kpis: DashboardKPIs = {
    totalPipeline: raw.kpis.total_pipeline,
    activeDeals: raw.kpis.active_deals,
    approvalRate: raw.kpis.approval_rate,
    totalVolume: raw.kpis.total_volume,
    totalBrokers: raw.kpis.total_brokers,
  };

  return { pipeline, portfolio, analytics: raw.analytics, kpis };
}

async function fetchFunderDashboard(days: number): Promise<FunderDashboardData> {
  const { data } = await api.get<RawResponse>(`/decisions/funder-dashboard/?days=${days}`);
  return transformResponse(data);
}

export function useFunderDashboardQuery(days = 30) {
  return useQuery({
    queryKey: queryKeys.funderDashboard(days),
    queryFn: () => fetchFunderDashboard(days),
    staleTime: 60_000,
  });
}
